import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Grid,
  Message,
  Dropdown,
  Icon,
  Select,
  Input,
  Checkbox,
  Modal,
} from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  setErrorMessage,
  fetchApps,
  fetchDeploymentTypes,
  resetFormAction,
} from "./reducer/actions";
import { DeploymentType, MarketplaceAppProvisionApiResponse } from "./types";
import FetchAPI from "../api/FetchAPI";
import FancyHeader from "../components/shared/FancyHeader";
import { closeSlidingMenuLayer } from "../actions/toggleSlidingMenu";
import {
  renderZonesForSelectBox,
  getCurrentProjectID,
  get_FormItem_ClassName,
  handleScrollToItem,
} from "../app_shared_functions";
import useIsMountedRef from "../custom-hooks/useIsMountedRef";
import MonthlyPrice from "../openstack/servers/create/price/MonthlyPrice";
import MarketplaceAppSchemaForm from "./marketplace-app-form/MarketplaceAppSchemaForm";
import CreateButtons from "../components/shared/form/CreateButtons";
import { State } from "../selectors/state";
import useMarketplaceAppForm from "./marketplace-app-form/hooks/useMarketplaceAppForm";
import { OpenStackRegion } from "../api/resources/AccessControlOpenStack/Projects";
import { getAvailableRegions } from "../selectors/regions";

const MarketplaceAppSlidingPanel: React.FC = () => {
  const dispatch = useDispatch();
  const { selectedApp, deploymentTypes, errorMessage } = useSelector(
    (state: State) => state.marketplace,
  );
  const projects = useSelector((state: State) => state.projects);
  const userDomains = useSelector(
    (state: State) => state.usersettings?.selectedDomains || null,
  );
  const regions = useSelector(getAvailableRegions);

  const [schema, setSchema] = useState<{ [key: string]: any }>({});
  const [invalidForm, setInvalidForm] = useState(false);
  const [shake, setShake] = useState(false);
  const [showMonthlyPrice, setShowMonthlyPrice] = useState(false);
  const [showDeploymentTypeRow, setShowDeploymentTypeRow] = useState(true);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTCModalOpen, setIsTCModalOpen] = useState(false);

  const zoneRef = useRef<HTMLElement | null>(null);
  const isMountedRef = useIsMountedRef();

  const extendedSchema = useMemo(() => {
    return {
      ...schema,
      properties: {
        ...(schema.properties || {}),
        region: { type: "string", title: "Region" },
        deploymentType: { type: "number", title: "Deployment Type" },
        termsAccepted: {
          type: "boolean",
          title: "Terms and Conditions",
          enum: [true],
          errorMessage: {
            enum: "You must accept the Terms and Conditions",
          },
        },
      },

      required: [
        ...(schema.required || []),
        "region",
        "deploymentType",
        "termsAccepted",
      ],
    };
  }, [schema]);

  const { formData, errors, handleChange, validateForm } =
    useMarketplaceAppForm({
      schema: extendedSchema,
      initialValues: { termsAccepted: false },
    });

  useEffect(() => {
    if (selectedApp) {
      dispatch(fetchDeploymentTypes(selectedApp.id));
    }
    return () => {
      isMountedRef.current = false;
    };
  }, [dispatch, selectedApp, isMountedRef]);

  useEffect(() => {
    if (formData.region && deploymentTypes.length === 1) {
      setShowDeploymentTypeRow(false);
      const singleDeploymentType = deploymentTypes[0];
      handleChange("deploymentType", singleDeploymentType.id);
      setSchema(singleDeploymentType.requiredAttributes);
    } else {
      setShowDeploymentTypeRow(deploymentTypes.length !== 1);
    }
  }, [formData.region, deploymentTypes, handleChange]);

  useEffect(() => {
    if (formData?.image && formData?.flavor?.id && formData.region) {
      setShowMonthlyPrice(true);
    } else {
      setShowMonthlyPrice(false);
    }
  }, [formData]);

  const handleDeploymentTypeChange = useCallback(
    (id: number) => {
      handleChange("deploymentType", id);
      const selectedType = deploymentTypes.find(
        (type: DeploymentType) => type.id === id,
      );
      if (selectedType) {
        setSchema(selectedType.requiredAttributes);
      }
    },
    [deploymentTypes, handleChange],
  );

  const handleProvisionError = useCallback(
    (response: MarketplaceAppProvisionApiResponse) => {
      if (isMountedRef.current) {
        const msg = response.message ?? "An error occurred";
        toast.error(msg);
        dispatch(setErrorMessage(msg));
      }
    },
    [dispatch, isMountedRef],
  );

  const projectId = useMemo(() => {
    if (formData.region) {
      const currentProject = getCurrentProjectID(
        projects,
        formData.region.toLowerCase(),
      );
      if (!currentProject) {
        console.error("Unable to fetch the current project");
        toast.error("An unexpected error occurred. Please try again.");
      }
      return currentProject || "";
    }
    return "";
  }, [projects, formData.region]);

  const handleFormSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (isSubmitting) return;
      setIsSubmitting(true);

      const convertedFormData = { ...formData };
      Object.keys(schema.properties || {}).forEach((key) => {
        if (schema.properties[key].type === "integer") {
          convertedFormData[key] = Number(formData[key]);
        }

        if (key === "use_extraroute") {
          const region = regions.find(
            (region: OpenStackRegion) =>
              region.tag === convertedFormData.region,
          );

          if (region === undefined) {
            toast.error("You need to select a valid region");
            setIsSubmitting(false);
            return;
          }

          if (region.network_provider === "ovn") {
            convertedFormData.use_extraroute = true;
          }
        }
      });

      delete convertedFormData.region;
      delete convertedFormData.deploymentType;
      delete convertedFormData.termsAccepted;

      if (!selectedApp || !formData.deploymentType || !projectId) {
        toast.error("Please complete all required fields.");
        setIsSubmitting(false);
        return;
      }

      const response = await FetchAPI.Marketplace.provisionMarketplaceApp(
        projectId,
        formData.region,
        selectedApp.id,
        formData.deploymentType,
        convertedFormData,
      );

      if (isMountedRef.current) {
        if (response.status === "success") {
          toast.success(
            <div className="bg-white text-black">
              {response.message || "App provisioning in progress."}
              <br />
              <Link
                to="/marketplace/provisioned"
                className="color-white underline"
              >
                Click here to view
              </Link>
            </div>,
          );
          dispatch(closeSlidingMenuLayer());
          dispatch(fetchApps());
          dispatch(resetFormAction());
          setIsSubmitting(false);
        } else if (response.status === "error") {
          handleProvisionError(response);
          setIsSubmitting(false);
        } else {
          toast.error("An unexpected error occurred. Please try again.");
          setIsSubmitting(false);
        }
      }
    },
    [
      dispatch,
      isSubmitting,
      selectedApp,
      schema.properties,
      formData,
      handleProvisionError,
      projectId,
      isMountedRef,
    ],
  );

  const combinedError = useMemo(() => {
    const messages: string[] = [];
    const primaryRef: HTMLElement | null = zoneRef.current || document.body;
    Object.keys(errors).forEach((key) => {
      const fieldErrors = errors[key];
      if (fieldErrors.length > 0) {
        const title = extendedSchema.properties?.[key]?.title || key;
        messages.push(...fieldErrors.map((msg: string) => `${title}: ${msg}`));
      }
    });
    return messages.length > 0
      ? { message: messages, ref: primaryRef }
      : undefined;
  }, [errors, extendedSchema.properties]);

  const emptyRequiredFields = useMemo(() => {
    if (!extendedSchema || !extendedSchema.required) return [];
    return extendedSchema.required.filter((field: string) => !formData[field]);
  }, [extendedSchema, formData]);

  const openTCModal = useCallback(() => {
    setIsTCModalOpen(true);
  }, []);

  const closeTCModal = useCallback(() => {
    setIsTCModalOpen(false);
  }, []);

  const handleCreate = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (isSubmitting) return;
      const isValid = validateForm();
      if (!isValid) {
        toast.error("Validation errors. Please check the form fields.");
        setInvalidForm(true);
        setShake(true);
        handleScrollToItem(zoneRef.current, true);
        setTimeout(() => setShake(false), 1000);
        return;
      }
      handleFormSubmit(event);
    },
    [validateForm, isSubmitting, handleFormSubmit],
  );

  const deploymentTypeText = useMemo(() => {
    if (formData.deploymentType) {
      const selected = deploymentTypes.find(
        (type: DeploymentType) => type.id === formData.deploymentType,
      );
      return selected ? selected.title : "Select a Deployment Type";
    }
    return "Select a Deployment Type";
  }, [formData.deploymentType, deploymentTypes]);

  const areasList = useMemo(
    () => renderZonesForSelectBox(projects, userDomains),
    [projects, userDomains],
  );

  const handleRegionChange = useCallback(
    (_, { value }) => {
      handleChange("region", value || "");
      handleChange("deploymentType", "");
    },
    [handleChange],
  );

  const onDeploymentTypeClick = useCallback(
    (id: number) => () => handleDeploymentTypeChange(id),
    [handleDeploymentTypeChange],
  );

  const handleClose = useCallback(() => {
    dispatch(closeSlidingMenuLayer());
  }, [dispatch]);

  const handleToggleAdvanced = useCallback(() => {
    setIsAdvancedOpen((prev) => !prev);
  }, []);

  const hasAdvancedFields = useMemo(() => {
    return (
      schema.properties &&
      Object.keys(schema.properties).some(
        (key) => schema.properties[key].advancedModeView,
      )
    );
  }, [schema.properties]);

  const computedTotalVolume = useMemo<number>(() => {
    if (!schema?.properties || !showMonthlyPrice) return 0;
    return Object.keys(schema.properties).reduce((total, key) => {
      if (schema.properties[key]?.volumePrice) {
        return total + Number(formData[key] || 0);
      }
      return total;
    }, 0);
  }, [formData, schema, showMonthlyPrice]);

  if (!selectedApp) return null;

  return (
    <>
      <FancyHeader title={`${selectedApp.title} Installation`} />
      {showMonthlyPrice && (
        <>
          <MonthlyPrice
            regionId={formData.region}
            flavorId={formData?.flavor?.id}
            volumeSize={computedTotalVolume}
            imageId={formData?.image}
            floatingIp={Boolean(formData?.externalNetwork)}
            disasterRecovery={Boolean(formData?.disasterRecovery)}
            storageClass={false}
          />
          <p className="padding-top-50" />
        </>
      )}
      <p></p>
      {errorMessage && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{errorMessage}</p>
        </Message>
      )}
      <Grid>
        <Grid.Row>
          <Grid.Column className="flex vcenter" textAlign="left" width={8}>
            <h5>
              Choose Region
              <span className="color-red padding-left-half bold">*</span>
            </h5>
          </Grid.Column>
          <Grid.Column textAlign="right" width={8}>
            <Select
              icon="chevron circle down"
              className={get_FormItem_ClassName(
                "",
                invalidForm,
                "zoneRef",
                shake,
                "error-form-item",
              )}
              placeholder="Choose Region"
              options={areasList}
              onChange={handleRegionChange}
              value={formData.region || ""}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      {showDeploymentTypeRow && (
        <Grid className="display-none">
          <Grid.Row>
            <Grid.Column className="flex vcenter" textAlign="left" width={8}>
              <h5>
                Choose Deployment Type
                <span className="color-red padding-left-half bold">*</span>
              </h5>
            </Grid.Column>
            <Grid.Column textAlign="right" width={8}>
              {showDeploymentTypeRow ? (
                <Dropdown
                  scrolling
                  disabled={!formData.region}
                  value={formData.deploymentType}
                  text={deploymentTypeText}
                  icon="chevron circle down"
                  className="select-box full"
                >
                  <Dropdown.Menu className="overflow-auto">
                    {deploymentTypes.map((type: DeploymentType) => (
                      <Dropdown.Item
                        key={type.id}
                        text={type.title}
                        value={type.id}
                        onClick={onDeploymentTypeClick(type.id)}
                      />
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              ) : (
                <Input
                  className="select-box full"
                  value={deploymentTypeText}
                  disabled
                />
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      )}
      {schema.properties && (
        <MarketplaceAppSchemaForm
          schema={schema}
          formData={formData}
          formErrors={errors}
          onFormChange={handleChange}
          isAdvanced={false}
        />
      )}
      {hasAdvancedFields && formData.region && (
        <>
          <div className="margin-bottom-40" />
          <Grid>
            <Grid.Row>
              <Grid.Column textAlign="left" width={16}>
                <h4
                  className="padding-top flex cursor_pointer"
                  onClick={handleToggleAdvanced}
                >
                  {isAdvancedOpen ? (
                    <Icon
                      className="advanced_icon"
                      name="chevron circle down"
                    />
                  ) : (
                    <Icon
                      className="advanced_icon"
                      name="chevron circle right"
                    />
                  )}
                  <span>Advanced Options</span>
                </h4>
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <div className={isAdvancedOpen ? "" : "display-none"}>
            <MarketplaceAppSchemaForm
              schema={schema}
              formData={formData}
              formErrors={errors}
              onFormChange={handleChange}
              isAdvanced={true}
            />
          </div>
        </>
      )}
      <div className="separator"></div>
      <div className="margin-top-30">
        <React.Fragment>
          <Grid.Column textAlign="left" width={16}>
            <Checkbox
              className="simple-checkbox margin-top-half"
              label={{
                children: (
                  <>
                    <p>
                      I agree to the{" "}
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          openTCModal();
                        }}
                      >
                        Terms and Conditions
                      </a>
                    </p>
                  </>
                ),
              }}
              checked={formData.termsAccepted || false}
              onChange={(_e, data) =>
                handleChange("termsAccepted", data.checked ?? false)
              }
              required
            />
          </Grid.Column>
        </React.Fragment>
      </div>

      <div className="separator" />
      <div className="margin-top-30 margin-bottom-30">
        <CreateButtons
          error={combinedError}
          emptyRequiredFields={emptyRequiredFields}
          showFormWarning={setInvalidForm}
          action={handleCreate}
          isCreating={isSubmitting}
          closeSlidingMenuLayer={handleClose}
          createAnother={false}
          changeCreateAnother={undefined}
        />
      </div>

      <Modal open={isTCModalOpen} onClose={closeTCModal} closeIcon>
        <Modal.Header>Third Party Solutions</Modal.Header>
        <Modal.Content scrolling>
          <p>
            Customer may use certain third party solutions together with the
            Services, such as third-party applications and services on Cleura
            Marketplace or which Customer otherwise chooses to install or
            integrate (“Third Party Solutions”). Third Party Solutions are
            governed by third party terms, such as license agreements, terms of
            service and data protection terms and notices. Customer’s use of
            Third Party Solutions is solely between Customer and the applicable
            Third Party Solutions provider, and it is Customer’s responsibility
            to identify and comply with applicable third party terms. Unless
            otherwise agreed in writing, Customer agrees to assume all risk and
            liability related to Third Party Solutions, Cleura is not
            responsible or liable for Third-Party Solutions or the information
            in Cleura Marketplace, and Cleura may remove or prohibit use of
            solutions from Cleura Marketplace at any time. Cleura may change its
            terms relating to Third Party Solutions at any time.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <button className="button button--orange" onClick={closeTCModal}>
            Close
          </button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default MarketplaceAppSlidingPanel;
